import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi } from '../../services/iso20022Api';
import {
  FileText,
  Search,
  Filter,
  Download,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  RefreshCw,
  FileCode
} from 'lucide-react';
import { formatDate, debounce } from '../../utils/helpers';
import XmlViewerModal from '../../components/iso20022/XmlViewerModal';

/**
 * RTP Status Messages (pain.014) Page
 * Displays Request-to-Pay status/response messages
 * 
 * Features:
 * - Statistics dashboard with key metrics
 * - Advanced filtering (status, direction, date range, search)
 * - Quick XML viewer modal
 * - Related message linking
 * - Sortable table with status badges
 */
const RtpStatuses = () => {
  const [statuses, setStatuses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    grpSts: '',
    txSts: '',
    direction: '',
    startDate: '',
    endDate: '',
    search: '',
    limit: 100
  });

  // UI states
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [xmlModalOpen, setXmlModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);

  // Load data on mount and when filters change
  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load stats and statuses in parallel
      const [statsData, statusesData] = await Promise.all([
        dashboardApi.getRtpStatusStats(),
        dashboardApi.getRtpStatusTransactions(filters)
      ]);

      console.log('[Load RTP Statuses] Count:', statusesData?.length ?? 0);
      setStats(statsData);
      setStatuses(statusesData);
    } catch (err) {
      console.error('[Load RTP Statuses] Error:', err);
      setError('Failed to load RTP Status data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearchChange = debounce((value) => {
    setFilters(prev => ({ ...prev, search: value }));
  }, 300);

  const handleClearFilters = () => {
    setFilters({
      grpSts: '',
      txSts: '',
      direction: '',
      startDate: '',
      endDate: '',
      search: '',
      limit: 100
    });
  };

  const handleViewXml = (status) => {
    setSelectedStatus(status);
    setXmlModalOpen(true);
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    alert('Export functionality coming soon');
  };

  const getStatusBadge = (status) => {
    const badges = {
      'ACTC': { color: 'success', icon: CheckCircle2, label: 'Accepted' },
      'ACSC': { color: 'teal', icon: CheckCircle2, label: 'Settled' },
      'RJCT': { color: 'danger', icon: XCircle, label: 'Rejected' },
      'PDNG': { color: 'yellow', icon: Clock, label: 'Pending' },
      'PART': { color: 'orange', icon: AlertCircle, label: 'Partial' },
      'ACCP': { color: 'success', icon: CheckCircle2, label: 'Profile OK' },
      'ACSP': { color: 'blue', icon: RefreshCw, label: 'In Process' },
      'ACWC': { color: 'purple', icon: CheckCircle2, label: 'With Change' },
      'RCVD': { color: 'gray', icon: FileText, label: 'Received' }
    };

    const badge = badges[status] || { color: 'gray', icon: FileText, label: status };
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${badge.color}-100 text-${badge.color}-800`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const getDirectionBadge = (direction) => {
    const badges = {
      'OUTGOING_SIGNED': { color: 'blue', label: 'Outgoing' },
      'OUTGOING_UNSIGNED': { color: 'indigo', label: 'Outgoing' },
      'INCOMING': { color: 'success', label: 'Incoming' }
    };

    const badge = badges[direction] || { color: 'gray', label: direction };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${badge.color}-100 text-${badge.color}-800`}>
        {badge.label}
      </span>
    );
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
          <p className="text-gray-600">Loading RTP Status messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">RTP Status Messages</h1>
          <p className="text-gray-600 mt-1">Request-to-Pay status and response messages (pain.014)</p>
        </div>
        <button
          onClick={handleExport}
          className="btn-secondary flex items-center"
        >
          <Download className="w-4 h-4 mr-2" />
          Export
        </button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Statuses</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Today: {stats.todayCount || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-primary-600" />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Accepted</p>
                  <p className="text-2xl font-bold text-success-600 mt-1">{stats.accepted || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Rate: {stats.acceptanceRate || 0}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-success-600" />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Rejected</p>
                  <p className="text-2xl font-bold text-danger-600 mt-1">{stats.rejected || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Rate: {stats.rejectionRate || 0}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-danger-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-danger-600" />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.pending || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Unlinked: {stats.unlinkedCount || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-700">Filters</span>
            {(filters.grpSts || filters.direction || filters.startDate || filters.endDate || filters.search) && (
              <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                Active
              </span>
            )}
          </div>
          {filtersExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </div>

        {filtersExpanded && (
          <div className="p-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    defaultValue={filters.search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search by ID, party name..."
                    className="input pl-10"
                  />
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              {/* Group Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Status
                </label>
                <select
                  value={filters.grpSts}
                  onChange={(e) => handleFilterChange('grpSts', e.target.value)}
                  className="input"
                >
                  <option value="">All Statuses</option>
                  <option value="ACTC">Accepted (ACTC)</option>
                  <option value="ACSC">Settled (ACSC)</option>
                  <option value="RJCT">Rejected (RJCT)</option>
                  <option value="PDNG">Pending (PDNG)</option>
                  <option value="PART">Partial (PART)</option>
                  <option value="ACCP">Profile OK (ACCP)</option>
                  <option value="ACSP">In Process (ACSP)</option>
                  <option value="ACWC">With Change (ACWC)</option>
                  <option value="RCVD">Received (RCVD)</option>
                </select>
              </div>

              {/* Direction Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Direction
                </label>
                <select
                  value={filters.direction}
                  onChange={(e) => handleFilterChange('direction', e.target.value)}
                  className="input"
                >
                  <option value="">All Directions</option>
                  <option value="OUTGOING_SIGNED">Outgoing (Signed)</option>
                  <option value="OUTGOING_UNSIGNED">Outgoing (Unsigned)</option>
                  <option value="INCOMING">Incoming</option>
                </select>
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="input"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="input"
                />
              </div>

              {/* Limit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Limit
                </label>
                <select
                  value={filters.limit}
                  onChange={(e) => handleFilterChange('limit', Number(e.target.value))}
                  className="input"
                >
                  <option value={50}>50 results</option>
                  <option value={100}>100 results</option>
                  <option value={250}>250 results</option>
                  <option value={500}>500 results</option>
                  <option value={1000}>1000 results</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex items-center space-x-3">
              <button onClick={loadData} className="btn-primary">
                <Search className="w-4 h-4 mr-2" />
                Apply Filters
              </button>
              <button onClick={handleClearFilters} className="btn-secondary">
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="card bg-danger-50 border border-danger-200">
          <div className="card-body">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-danger-600 mr-2" />
              <p className="text-danger-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Status Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Message ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Original Request
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Direction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Party
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Decision Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    <RefreshCw className="w-8 h-8 text-primary-600 animate-spin mx-auto mb-2" />
                    <p>Loading...</p>
                  </td>
                </tr>
              ) : statuses.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-900 font-medium mb-2">No RTP status messages found</p>
                    {(filters.grpSts || filters.direction || filters.search) && (
                      <button
                        onClick={handleClearFilters}
                        className="text-primary-600 hover:text-primary-700 text-sm"
                      >
                        Clear filters to see all messages
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                statuses.map((status) => (
                  <tr key={status.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 font-mono">
                        {status.msgId}
                      </div>
                      <div className="text-xs text-gray-500">
                        ID: {status.id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-mono">
                        {status.orgnlMsgId}
                      </div>
                      {status.orgnlEndToEndId && (
                        <div className="text-xs text-gray-500 font-mono">
                          E2E: {status.orgnlEndToEndId}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {getStatusBadge(status.grpSts || status.txSts)}
                        {status.grpStsRsn && (
                          <div className="text-xs text-gray-500 max-w-xs truncate">
                            {status.grpStsRsn}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getDirectionBadge(status.direction)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {status.initgPtyName || 'N/A'}
                      </div>
                      {status.dbtrAgtBicfi && (
                        <div className="text-xs text-gray-500">
                          Bank: {status.dbtrAgtBicfi}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {status.dbtrDcsnDtTm ? formatDate(status.dbtrDcsnDtTm, 'PPpp') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(status.createdAt, 'PPpp')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {status.rawXml && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewXml(status);
                            }}
                            className="text-primary-600 hover:text-primary-900"
                            title="View XML"
                          >
                            <FileCode className="w-4 h-4" />
                          </button>
                        )}
                        <Link
                          to={`/iso20022/rtp-statuses/${status.id}`}
                          className="text-primary-600 hover:text-primary-900 inline-flex items-center"
                        >
                          View
                          <ExternalLink className="w-4 h-4 ml-1" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Info */}
        {statuses.length > 0 && (
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{statuses.length}</span> results
              {filters.limit < (stats?.total || 0) && (
                <span> (limited to {filters.limit} from {stats?.total || 0} total)</span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* XML Viewer Modal */}
      <XmlViewerModal
        isOpen={xmlModalOpen}
        onClose={() => setXmlModalOpen(false)}
        xml={selectedStatus?.rawXml}
        title={`RTP Status: ${selectedStatus?.msgId}`}
      />
    </div>
  );
};

export default RtpStatuses;
